import { ROOM_SPACES } from './roomTimeline.mjs';

function createImpulse(context, seconds = 1.6, decay = 2.7) {
  const length = Math.floor(context.sampleRate * seconds);
  const impulse = context.createBuffer(2, length, context.sampleRate);
  for (let channel = 0; channel < impulse.numberOfChannels; channel += 1) {
    const data = impulse.getChannelData(channel);
    for (let index = 0; index < length; index += 1) {
      const envelope = Math.pow(1 - index / length, decay);
      data[index] = (Math.random() * 2 - 1) * envelope;
    }
  }
  return impulse;
}

function createRoomTone(context) {
  const seconds = 3;
  const buffer = context.createBuffer(1, context.sampleRate * seconds, context.sampleRate);
  const data = buffer.getChannelData(0);
  let previous = 0;
  for (let index = 0; index < data.length; index += 1) {
    const white = Math.random() * 2 - 1;
    previous = previous * 0.985 + white * 0.015;
    data[index] = previous;
  }
  return buffer;
}

export class RoomAudioEngine {
  constructor(context) {
    this.context = context;
    this.sources = [];
    this.timers = [];
    this.startedAt = 0;
    this.timeline = null;
    this.playing = false;
    this.suspended = false;
  }

  async decode(bytes) {
    return this.context.decodeAudioData(bytes.slice(0));
  }

  async play(scene, timeline, onEnded) {
    this.stop();
    await this.context.resume();
    this.timeline = timeline;
    const context = this.context;
    const now = context.currentTime + 0.14;
    this.startedAt = now;

    const master = context.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.82, now + 0.18);
    master.gain.setValueAtTime(0.82, Math.max(now + 0.18, now + timeline.duration - 0.5));
    master.gain.exponentialRampToValueAtTime(0.0001, now + timeline.duration);

    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -18;
    compressor.knee.value = 16;
    compressor.ratio.value = 2.2;
    compressor.attack.value = 0.008;
    compressor.release.value = 0.2;
    master.connect(compressor).connect(context.destination);

    const reverb = context.createConvolver();
    reverb.buffer = createImpulse(context);
    const reverbReturn = context.createGain();
    reverbReturn.gain.value = 0.62;
    reverb.connect(reverbReturn).connect(master);

    const roomTone = context.createBufferSource();
    roomTone.buffer = createRoomTone(context);
    roomTone.loop = true;
    const roomFilter = context.createBiquadFilter();
    roomFilter.type = 'lowpass';
    roomFilter.frequency.value = scene.room_presence === 'airy' ? 2600 : 1700;
    const roomGain = context.createGain();
    const toneLevel = scene.room_presence === 'close' ? 0.011 : scene.room_presence === 'airy' ? 0.007 : 0.005;
    roomGain.gain.setValueAtTime(0.0001, now);
    roomGain.gain.exponentialRampToValueAtTime(toneLevel, now + 0.7);
    roomGain.gain.setValueAtTime(toneLevel, Math.max(now + 0.7, now + timeline.duration - 0.8));
    roomGain.gain.exponentialRampToValueAtTime(0.0001, now + timeline.duration);
    roomTone.connect(roomFilter).connect(roomGain).connect(master);
    roomTone.start(now);
    roomTone.stop(now + timeline.duration + 0.05);
    this.sources.push(roomTone);

    const speakerMap = new Map((scene.speakers || []).map((speaker) => [speaker.id, speaker]));
    for (const event of timeline.events) {
      const speaker = speakerMap.get(event.speaker_id);
      const space = ROOM_SPACES[speaker?.space] || ROOM_SPACES.center;
      const start = now + event.start;
      const source = context.createBufferSource();
      source.buffer = event.clip.buffer;

      const envelope = context.createGain();
      envelope.gain.setValueAtTime(0.0001, start);
      envelope.gain.exponentialRampToValueAtTime(1, start + 0.018);
      const fadeAt = Math.max(start + 0.03, start + event.duration - 0.045);
      envelope.gain.setValueAtTime(1, fadeAt);
      envelope.gain.exponentialRampToValueAtTime(0.0001, start + event.duration);

      const distanceFilter = context.createBiquadFilter();
      distanceFilter.type = 'lowpass';
      distanceFilter.frequency.value = space.cutoff;
      distanceFilter.Q.value = 0.38;

      const direct = context.createGain();
      direct.gain.value = space.direct * (event.mixGain || 1);
      const panner = context.createStereoPanner();
      panner.pan.value = space.pan;
      const send = context.createGain();
      send.gain.value = space.reverb * (event.mixGain || 1);

      source.connect(envelope).connect(distanceFilter);
      distanceFilter.connect(direct).connect(panner).connect(master);
      distanceFilter.connect(send).connect(reverb);
      source.start(start);
      source.stop(start + event.duration + 0.03);
      this.sources.push(source);
    }

    this.playing = true;
    this.suspended = false;
    const timer = setTimeout(() => {
      this.playing = false;
      onEnded?.();
    }, (timeline.duration + 0.2) * 1000);
    this.timers.push(timer);
  }

  elapsed() {
    if (!this.timeline || !this.startedAt) return 0;
    return Math.max(0, Math.min(this.timeline.duration, this.context.currentTime - this.startedAt));
  }

  async togglePause() {
    if (!this.playing) return false;
    if (this.context.state === 'running') {
      await this.context.suspend();
      this.suspended = true;
      return true;
    }
    await this.context.resume();
    this.suspended = false;
    return false;
  }

  stop() {
    for (const timer of this.timers) clearTimeout(timer);
    this.timers = [];
    for (const source of this.sources) {
      try { source.stop(); } catch { /* Source may already have ended. */ }
      try { source.disconnect(); } catch { /* It may already be disconnected. */ }
    }
    this.sources = [];
    this.playing = false;
    this.suspended = false;
  }
}
